import * as React from 'react';

import { clsx } from '@freelancelabs/utils';

import TableHeaderCell, { TableHeaderCellProps } from './TableHeaderCell';

export interface TableHeaderProps extends React.HTMLAttributes<HTMLTableSectionElement> {
    sticky?: boolean;
    cells?: TableHeaderCellProps[];
    children?: React.ReactNode;
    /**
     * Rend chaque en-tête redimensionnable, en tirant sa bordure droite.
     *
     * Attention, cette prop est ce qui rend le mécanisme UTILISABLE, et il
     * existait bien avant elle. `TableHeaderCell` porte le tirage depuis
     * toujours, et les styles aussi, mais l'activer demandait à l'écran appelant
     * de tenir lui-même l'état de la colonne tirée ET de poser `resizer` sur
     * chacune de ses cellules. Aucun écran ne l'a jamais fait : un mécanisme qui
     * demande trois gestes pour servir est un mécanisme qui dort.
     *
     * Attention, la largeur vit DANS LA CELLULE et ne survit ni au changement de
     * page, ni au tri, ni au filtre qui remonte le tableau. C'est un confort de
     * lecture immédiat et non une préférence gardée, à la différence du choix
     * des colonnes, qui lui est rangé dans le navigateur (PCG-135). Les deux se
     * ressemblent à l'usage et ne promettent pas la même chose.
     */
    resizable?: boolean;
    /**
     * La colonne actuellement survolée ou tirée, quand l'écran veut tenir cet
     * état lui-même. Sans elle, `resizable` suffit et l'état est interne.
     */
    mouseDown?: string;
    setMouseDown?: (arg: string | null) => void;
}

const TableHeader = ({
    cells = [],
    children,
    sticky = false,
    resizable = false,
    mouseDown,
    setMouseDown,
    ...rest
}: TableHeaderProps) => {
    /**
     * L'état de la colonne tirée, tenu ici quand l'appelant ne le tient pas.
     *
     * Il est UNIQUE pour tout l'en-tête, et il le doit : deux colonnes tirées en
     * même temps n'ont aucun sens, et c'est aussi ce qui n'allume la poignée que
     * sous le curseur. Le nom vient de l'API d'origine ; il décrit la colonne
     * sous la souris, tirée ou seulement survolée.
     */
    const [ownMouseDown, setOwnMouseDown] = React.useState<string | null>(null);
    const activeColumn = mouseDown ?? ownMouseDown ?? undefined;
    const onActiveColumn = setMouseDown ?? setOwnMouseDown;

    const childrenWithProps = React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
            return React.cloneElement(child as React.ReactElement<any>, {
                mouseDown: activeColumn,
                setMouseDown: onActiveColumn,
                // Attention, `resizer` ne recouvre pas ce que l'enfant déclare :
                // une colonne peut se refuser au tirage (la dernière d'un
                // tableau, ou une colonne d'action) en posant `resizer={false}`.
                ...(resizable && (child.props as TableHeaderCellProps)?.resizer === undefined ? { resizer: true } : {}),
            });
        }
        return child;
    });

    return (
        <thead className={clsx('fl-table-header', sticky && 'is-sticky')} {...rest}>
            <tr className="fl-table-header-line">
                {childrenWithProps || (
                    <>
                        {cells.map((cell, index) => (
                            <TableHeaderCell
                                mouseDown={activeColumn}
                                setMouseDown={onActiveColumn}
                                key={index.toString()}
                                {...cell}
                                // APRES l'etalement, sans quoi un `resizer`
                                // absent de la cellule ecraserait la valeur par
                                // `undefined` et le tirage ne s'activerait
                                // jamais. Une cellule qui se refuse au tirage
                                // pose `resizer: false`, qui l'emporte.
                                resizer={cell.resizer ?? resizable}
                            />
                        ))}
                    </>
                )}
            </tr>
        </thead>
    );
};

export default TableHeader;
